/**
 * Freeze / hang diagnostics for raid combat.
 *
 * Browser / Unity WebView console: filter `[raid-freeze]`.
 * - Freeze-related **warnings are ON by default** (no flag needed).
 * - Verbose geometry dumps: add `?debugFreeze=1` to the URL.
 *
 * Vitest: call `raidFreezeDiag.setEnabled(true)` or rely on default warnings.
 */

export type RaidFreezeZoneSnap = {
  id: string;
  shape: string;
  danger: string;
  skillId?: string;
  x: number;
  y: number;
  range: number;
  halfWidth?: number;
  aimAngle?: number;
};

type FreezeHold = {
  sinceSim: number;
  lastLogSim: number;
  behaviorId: string;
};

const PREFIX = "[raid-freeze]";
const TICK_WARN_MS = 50;
const FREEZE_HOLD_WARN_SEC = 0.5;
const FREEZE_HOLD_RELOG_SEC = 1.0;

function readDebugFreezeFlag(): boolean {
  try {
    if (typeof globalThis === "undefined") return false;
    const loc = (globalThis as { location?: { search?: string } }).location;
    if (!loc?.search) return false;
    return new URLSearchParams(loc.search).get("debugFreeze") === "1";
  } catch {
    return false;
  }
}

let verboseOverride: boolean | null = null;
/** When true, emit info-level charge/geometry logs even without ?debugFreeze=1. */
let forceVerbose = false;

const freezeHoldByUnit = new Map<number, FreezeHold>();
let lastTickWarnAt = 0;

export const raidFreezeDiag = {
  /** Force verbose on/off (tests). null = URL flag. */
  setVerbose(v: boolean | null): void {
    verboseOverride = v;
  },

  /** Enable verbose charge/geometry logs without URL (tests / temporary). */
  setForceVerbose(v: boolean): void {
    forceVerbose = v;
  },

  isVerbose(): boolean {
    if (verboseOverride != null) return verboseOverride;
    return forceVerbose || readDebugFreezeFlag();
  },

  reset(): void {
    freezeHoldByUnit.clear();
    lastTickWarnAt = 0;
  },

  warn(tag: string, detail?: Record<string, unknown>): void {
    if (detail) console.warn(PREFIX, tag, detail);
    else console.warn(PREFIX, tag);
  },

  info(tag: string, detail?: Record<string, unknown>): void {
    if (!this.isVerbose()) return;
    if (detail) console.log(PREFIX, tag, detail);
    else console.log(PREFIX, tag);
  },

  /**
   * Call at end of each sim tick (or scene frame). Logs when wall time is high.
   * `iterations` optional ??use for nested loop guards.
   */
  noteTickDuration(
    label: string,
    ms: number,
    detail?: Record<string, unknown>,
  ): void {
    if (!(ms > TICK_WARN_MS)) return;
    // Throttle identical spam to ~2/sec wall clock when possible.
    const now =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();
    if (now - lastTickWarnAt < 200 && label === "tickRaid") return;
    lastTickWarnAt = now;
    this.warn("slow_tick", {
      label,
      ms: Math.round(ms * 10) / 10,
      thresholdMs: TICK_WARN_MS,
      ...detail,
    });
  },

  noteLoopGuard(tag: string, detail: Record<string, unknown>): void {
    this.warn(`loop_guard:${tag}`, detail);
  },

  noteException(where: string, err: unknown): void {
    const e = err instanceof Error ? err : new Error(String(err));
    console.error(PREFIX, `exception:${where}`, e.message, e.stack ?? "");
  },

  noteChargePhase(
    phase:
      | "windup_start"
      | "travel_start"
      | "travel_end"
      | "abort_bad_travel"
      | "abort_nan_pos",
    detail: Record<string, unknown>,
  ): void {
    const always =
      phase === "abort_bad_travel" || phase === "abort_nan_pos";
    const payload = { phase, ...detail };
    if (always) this.warn("charge", payload);
    else this.info("charge", payload);
    // Windup start is critical for circle-vs-line mismatch ??warn once-level.
    if (phase === "windup_start") {
      const landDist = Number(detail.landDist);
      const bodyLandCircle = Number(detail.landRadius) || 0;
      if (Number.isFinite(landDist) && landDist < bodyLandCircle * 2 + 8) {
        this.warn("charge_land_near_body", payload);
      }
      const zones = detail.zones as RaidFreezeZoneSnap[] | undefined;
      if (zones) {
        const shapes = [...new Set(zones.map((z) => z.shape))].sort();
        if (shapes.includes("circle") && shapes.includes("line")) {
          this.warn("charge_zone_circle_and_line", {
            chargerId: detail.chargerId,
            shapes,
            zones,
          });
        }
      }
    }
  },

  noteDodgeGoalNull(
    unitId: number,
    zones: readonly RaidFreezeZoneSnap[],
    detail?: Record<string, unknown>,
  ): void {
    this.warn("dodge_goal_null_in_zone", {
      unitId,
      zoneCount: zones.length,
      zones,
      ...detail,
    });
  },

  /**
   * Track danger_freeze (or stuck plant) held across ticks.
   * Call every decide with the selected behavior id; clears when left.
   */
  noteBehaviorHold(
    unitId: number,
    behaviorId: string | null | undefined,
    simTime: number,
    detail?: Record<string, unknown>,
  ): void {
    if (behaviorId !== "danger_freeze") {
      freezeHoldByUnit.delete(unitId);
      return;
    }
    let hold = freezeHoldByUnit.get(unitId);
    if (!hold) {
      hold = { sinceSim: simTime, lastLogSim: -1e9, behaviorId };
      freezeHoldByUnit.set(unitId, hold);
    }
    const held = simTime - hold.sinceSim;
    if (
      held >= FREEZE_HOLD_WARN_SEC &&
      simTime - hold.lastLogSim >= FREEZE_HOLD_RELOG_SEC
    ) {
      hold.lastLogSim = simTime;
      this.warn("danger_freeze_held", {
        unitId,
        heldSec: Math.round(held * 100) / 100,
        simTime,
        ...detail,
      });
    }
  },

  snapZones(
    zones: readonly {
      id: string;
      shape: string;
      danger: string;
      skillId?: string;
      x: number;
      y: number;
      range: number;
      halfWidth?: number;
      aimAngle?: number;
    }[],
  ): RaidFreezeZoneSnap[] {
    return zones.map((z) => ({
      id: z.id,
      shape: z.shape,
      danger: z.danger,
      skillId: z.skillId,
      x: Math.round(z.x * 10) / 10,
      y: Math.round(z.y * 10) / 10,
      range: Math.round(z.range * 10) / 10,
      ...(z.halfWidth != null
        ? { halfWidth: Math.round(z.halfWidth * 10) / 10 }
        : {}),
      ...(z.aimAngle != null
        ? { aimAngle: Math.round(z.aimAngle * 1000) / 1000 }
        : {}),
    }));
  },
};

/** Wall-clock now for tick timing (browser + node). */
export function raidFreezeNowMs(): number {
  if (typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }
  return Date.now();
}
